// composables/useTabbarVisibility.js
// Derives shell tabbar visibility from the current F7 route + auth/membership
// gating. Single source for the showTabbar decision so layouts can't drift
// (Playbook Section 5 row 8, audit HIGH-10/11). Null route resolves to false to avoid
// flashing the tabbar before the first routeChange fires.
//
// Club-aware: the app has TWO mutually-exclusive routable-tabs shells — the
// seller shell at `/` (TabbarNavigation) and the supplier shell at `/suppliers/`
// (SupplierTabbarNavigation). Each tabbar must show ONLY while its own shell is
// active; the seller tabbar must never bleed onto /suppliers/* and vice-versa
// (architecture doc Section 4.5). The club type is route-derived via
// clubTypeForRoute (useClubContext): the URL's namespace, or the user's own club
// on a club-neutral route (the notification center) — so a supplier there never
// gets the seller tabbar. This one composable owns both decisions.

import { computed } from 'vue';
import { CLUB_TYPES } from '@/clubs/manifest.js';
import { useAuth } from '@/composables/useAuth';
import { clubTypeForRoute } from '@/composables/useClubContext';
import { useAuthStore } from '@/stores/authStore.js';

export function useTabbarVisibility(currentRoute) {
  const { isAuthenticated, hasActiveMembership, isSupplierPreview } = useAuth();

  // Common gate for both tabbars: authed + (active membership OR a live supplier
  // preview — a collector/leader has no club membership, and without this the
  // preview's supplier shell has no way to switch tabs) + a resolved route
  // whose meta doesn't explicitly hide the tabbar.
  const baseVisible = computed(() => {
    if (!isAuthenticated.value) return false;
    if (!hasActiveMembership.value && isSupplierPreview?.value !== true) return false;
    if (!currentRoute.value) return false;
    return !currentRoute.value?.route?.meta?.hideTabbar;
  });

  const authStore = useAuthStore();
  const clubType = computed(() =>
    clubTypeForRoute(currentRoute.value, {
      supplierStage: authStore.supplierStage,
      isSupplier: authStore.isSupplier,
    }),
  );

  // Seller tabbar — visible on the seller shell only.
  const showTabbar = computed(
    () => baseVisible.value && clubType.value === CLUB_TYPES.SELLERS,
  );

  // Supplier tabbar — visible on the supplier shell only.
  const showSupplierTabbar = computed(
    () => baseVisible.value && clubType.value === CLUB_TYPES.SUPPLIERS,
  );

  return { showTabbar, showSupplierTabbar };
}
